
import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import ScoringPanel from "./ScoringPanel";
import ResultsTable from "./ResultsTable";
import { isChippingTest, isWedgingTest, isBunkerTest, isIronTest, isFairwayWoodTest, isDriverTest, getCurrentLevel } from "./scoring/utils";
import { BallTendency } from "@/hooks/level3/types";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Save } from "lucide-react";

interface ScoresState {
  [key: string]: (number | null)[];
}

interface TestInterfaceProps {
  selectedTest: string;
  currentPoint: string;
  currentAttempt: number;
  scores: ScoresState;
  onBackToTests: () => void;
  onFinish: () => void;
  onScoreChange: (score: number, tendency?: BallTendency) => void;
  onCancelLastEntry: () => void;
  getTestTitle: (testId: string) => string;
  getTestDescription: (testId: string, path?: string) => string;
  getTestPoints: (testId: string) => string[];
  getTestDistances: (testId: string) => Record<string, string>;
  getTableHeadTitle: () => string;
  getFormattedPoint: (point: string) => string;
  getPointTotal: (point: string) => number;
  getGrandTotal: () => number;
  levels: Record<string, string>;
}

const TestInterface: React.FC<TestInterfaceProps> = ({
  selectedTest,
  currentPoint,
  currentAttempt,
  scores,
  onBackToTests,
  onFinish,
  onScoreChange,
  onCancelLastEntry,
  getTestTitle,
  getTestDescription,
  getTestPoints,
  getTestDistances,
  getTableHeadTitle,
  getFormattedPoint,
  getPointTotal,
  getGrandTotal,
  levels
}) => {
  const { t } = useTranslation('training');
  
  // Déterminer le niveau actuel
  const currentLevel = getCurrentLevel();
  console.log("TestInterface - Current level:", currentLevel);
  
  const testIsChippingTest = isChippingTest(selectedTest);
  const testIsWedgingTest = isWedgingTest(selectedTest);
  const testIsBunkerTest = isBunkerTest(selectedTest);
  const testIsIronTest = isIronTest(selectedTest);
  const testIsFairwayWoodTest = isFairwayWoodTest(selectedTest);
  const testIsDriverTest = isDriverTest(selectedTest);
  
  // Always use 10 attempts for all tests
  const maxAttempts = 10;
  const testPoints = getTestPoints(selectedTest);
  const distance = getTestDistances(selectedTest)[currentPoint] || "";
  
  // Fix: Create explicit handler function for button click
  const handleBackClick = () => {
    console.log("Back button clicked, calling onBackToTests");
    onBackToTests();
  };

  console.log("Test interface - selectedTest:", selectedTest);
  console.log("Current point:", currentPoint);
  console.log("Current attempt:", currentAttempt);
  console.log("Test points:", testPoints);
  console.log("Scores:", scores);
  console.log("Is chipping test:", testIsChippingTest);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="mb-12"
    >
      <Card className="w-full mx-auto mt-2 md:mt-6">
        <CardHeader className="pb-2 md:pb-6">
          <div className="flex justify-between items-center">
            <Button 
              variant="outline" 
              className="mb-2"
              onClick={handleBackClick}
              type="button"
            >
              <ChevronLeft className="mr-1" /> {t('level3.interface.backToTests')}
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-xs text-green-600 bg-green-50 p-2 rounded-md flex items-center">
                    <Save className="h-4 w-4 mr-1" />
                    {t('level3.interface.autoSaveEnabled')}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('level3.interface.autoSaveTooltip')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <CardTitle className="text-xl md:text-2xl text-golf-700">{getTestTitle(selectedTest)}</CardTitle>
          <CardDescription className="text-base mt-2">
            {getTestDescription(selectedTest, window.location.pathname)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 md:space-y-6">
            <ScoringPanel 
              testId={selectedTest}
              currentPoint={currentPoint}
              currentAttempt={currentAttempt}
              maxAttempts={maxAttempts}
              distance={distance}
              onScoreChange={onScoreChange}
              onCancelLastEntry={onCancelLastEntry}
              isChippingTest={testIsChippingTest}
              currentLevel={currentLevel}
            />

            <ResultsTable 
              selectedTest={selectedTest}
              scores={scores}
              testPoints={testPoints}
              isChippingTest={testIsChippingTest}
              getTestDistances={getTestDistances}
              getTableHeadTitle={getTableHeadTitle}
              getFormattedPoint={getFormattedPoint}
              getPointTotal={getPointTotal}
              getGrandTotal={getGrandTotal}
              levels={levels}
            />

            <div className="flex justify-end mt-4">
              <Button onClick={onFinish} className="bg-golf-600 hover:bg-golf-700">
                {t('level3.interface.finish')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default TestInterface;

